import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchAnalytics,
  fetchAiInsights,
  fetchHealth,
  fetchMembers,
  fetchSuggestions,
  fetchAccounts,
  getUser,
  logout,
  UNAUTHORIZED_EVENT,
} from './api';
import type {
  AnalyticsResponse,
  HealthResponse,
  MemberInfo,
  SuggestionsResponse,
  Timeframe,
  UserInfo,
} from './types';
import { DEFAULT_MAIN_CATEGORIES } from './utils/categorization';
import { TimeframeToggle } from './components/TimeframeToggle';
import { SummaryCards } from './components/SummaryCards';
import { ErrorBoundary } from './components/ErrorBoundary';
import { SpendingBarChart } from './components/SpendingBarChart';
import { TrendLineChart } from './components/TrendLineChart';
import { SpendingPieChart } from './components/SpendingPieChart';
import { InsightsPanel } from './components/InsightsPanel';
import { FileUpload } from './components/FileUpload';
import { ErrorBanner } from './components/ErrorBanner';
import { LoginModal } from './components/LoginModal';
import { MembersModal } from './components/MembersModal';
import { ChangePasswordModal } from './components/ChangePasswordModal';
import { CategorizeModal } from './components/CategorizeModal';
import { SourcesPanel } from './components/SourcesPanel';
import { TransactionList } from './components/TransactionList';
import { AISettingsModal } from './components/AISettingsModal';
import { DebugPanel } from './components/DebugPanel';
import { FireflyHub } from './components/FireflyHub';
import { Button } from './components/Button';

interface ViewOption {
  label: string;
  tag: string | null;
}

export default function App() {
  const [timeframe, setTimeframe] = useState<Timeframe>('month');
  const [analytics, setAnalytics] = useState<AnalyticsResponse | null>(null);
  const [suggestions, setSuggestions] = useState<SuggestionsResponse | null>(null);
  const [aiInsights, setAiInsights] = useState<{ spending_traps?: string[]; investment_suggestions?: string[]; provider?: string; at?: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [suggestionsError, setSuggestionsError] = useState<string | null>(null);
  const [health, setHealth] = useState<HealthResponse | null>(null);

  const [user, setUserState] = useState<UserInfo | null>(() => getUser());
  const [members, setMembers] = useState<MemberInfo[]>([]);
  const [viewTag, setViewTag] = useState<string | null>(null);
  const [viewOptions, setViewOptions] = useState<ViewOption[]>([]);
  const [accounts, setAccounts] = useState<string[]>([]);
  const [accountFilter, setAccountFilter] = useState<string>('');

  const [loginOpen, setLoginOpen] = useState(false);
  const [loginMessage, setLoginMessage] = useState<string | null>(null);
  const [membersOpen, setMembersOpen] = useState(false);
  const [changePwOpen, setChangePwOpen] = useState(false);
  const [categorizeOpen, setCategorizeOpen] = useState(false);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [transactionsOpen, setTransactionsOpen] = useState(false);
  const [txFilter, setTxFilter] = useState<string | null>(null);
  const [aiSettingsOpen, setAiSettingsOpen] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);
  const [fireflyOpen, setFireflyOpen] = useState(false);
  const [sawLoginPrompt, setSawLoginPrompt] = useState(false);

  const [chartGroupBy, setChartGroupBy] = useState<'overview' | 'main' | 'category'>('overview');
  const categoryHierarchy = DEFAULT_MAIN_CATEGORIES;
  const justLoggedIn = useRef(false);

  // Head-only: build the view options (family / me / each member).
  useEffect(() => {
    if (user?.role === 'head' || user?.role === 'co-head') {
      const opts: ViewOption[] = [{ label: 'Family (all)', tag: null }];
      if (user.tag) opts.push({ label: 'Me', tag: user.tag });
      for (const m of members) opts.push({ label: m.name, tag: m.tag });
      setViewOptions(opts);
    } else {
      setViewOptions([]);
      setViewTag(null);
    }
  }, [user, members]);

  // Head loads the member list.
  useEffect(() => {
    if (user?.role === 'head' || user?.role === 'co-head') {
      fetchMembers()
        .then((res) => {
          if (res.ok) setMembers(res.members);
        })
        .catch(() => setMembers([]));
    } else {
      setMembers([]);
    }
  }, [user]);

  // Any 401 anywhere → clear state and reopen the login modal.
  useEffect(() => {
    const onUnauthorized = () => {
      // If the user just logged in, ignore 401s for 5 seconds (parallel
      // requests like loadAnalytics + loadSuggestions can both fire 401s).
      if (justLoggedIn.current) return;
      setUserState(null);
      setAnalytics(null);
      setSuggestions(null);
      setLoginMessage('Session expired or invalid — please log in again.');
      setLoginOpen(true);
    };
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, []);

  const loadAnalytics = useCallback(
    async (tf: Timeframe, tag: string | null, account?: string | null) => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchAnalytics(tf, tag, account);
        setAnalytics(data);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load analytics');
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const loadSuggestions = useCallback(async () => {
    setSuggestionsError(null);
    try {
      const data = await fetchSuggestions();
      setSuggestions(data);
    } catch (e) {
      setSuggestionsError(e instanceof Error ? e.message : 'Failed to load suggestions');
    }
  }, []);

  const loadHealth = useCallback(async () => {
    try {
      const h = await fetchHealth();
      setHealth(h);
    } catch {
      setHealth(null);
    }
  }, []);

  const loadAiInsights = useCallback(async () => {
    try {
      const data = await fetchAiInsights();
      setAiInsights(data.insights);
    } catch {
      setAiInsights(null);
    }
  }, []);

  useEffect(() => {
    void loadHealth();
  }, [loadHealth]);

  // Load distinct bank accounts for the account filter.
  useEffect(() => {
    if (user) {
      fetchAccounts()
        .then((r) => setAccounts(r.accounts.map((a) => a.number)))
        .catch(() => setAccounts([]));
    } else {
      setAccounts([]);
      setAccountFilter('');
    }
  }, [user]);

  useEffect(() => {
    void loadAnalytics(timeframe, viewTag, accountFilter);
  }, [timeframe, viewTag, accountFilter, loadAnalytics]);

  useEffect(() => {
    if (user) void loadSuggestions();
  }, [user, loadSuggestions]);

  useEffect(() => {
    if (user) void loadAiInsights();
  }, [user, loadAiInsights]);

  // AI insights polling: check every 5 minutes if suggestions need refresh
  useEffect(() => {
    if (!user) return;
    const POLL_MS = 5 * 60 * 1000; // 5 minutes
    const interval = setInterval(() => {
      void loadSuggestions();
    }, POLL_MS);
    return () => clearInterval(interval);
  }, [user, loadSuggestions]);

  // First run with no session: nudge the user to log in.
  useEffect(() => {
    if (!user && !sawLoginPrompt) {
      setSawLoginPrompt(true);
      const t = setTimeout(() => setLoginOpen(true), 400);
      return () => clearTimeout(t);
    }
  }, [user, sawLoginPrompt]);

  const handleIngestSuccess = useCallback(() => {
    void loadAnalytics(timeframe, viewTag, accountFilter);
    if (user) void loadSuggestions();
    void loadHealth();
  }, [loadAnalytics, loadSuggestions, loadHealth, timeframe, viewTag, user, accountFilter]);

  const handleLoggedIn = useCallback(() => {
    justLoggedIn.current = true;
    // Reset after 5s so real 401s (expired session) still work later.
    setTimeout(() => { justLoggedIn.current = false; }, 5000);
    setUserState(getUser());
    setLoginMessage(null);
    setAnalytics(null);
    setSuggestions(null);
    void loadHealth();
    void loadAnalytics(timeframe, viewTag, accountFilter);
    if ((user?.role === 'head' || user?.role === 'co-head') || (getUser()?.role === 'head' || getUser()?.role === 'co-head')) {
      fetchMembers()
        .then((res) => {
          if (res.ok) setMembers(res.members);
        })
        .catch(() => undefined);
    }
    void loadSuggestions();
  }, [loadAnalytics, loadSuggestions, loadHealth, timeframe, viewTag, user?.role]);

  const handleLogout = useCallback(() => {
    logout();
    setUserState(null);
    setAnalytics(null);
    setSuggestions(null);
    setMembers([]);
    setViewTag(null);
    setError(null);
    setLoginMessage(null);
    setLoginOpen(true);
  }, []);

  const handleMembersChanged = useCallback(() => {
    if (user?.role === 'head' || user?.role === 'co-head') {
      fetchMembers()
        .then((res) => {
          if (res.ok) setMembers(res.members);
        })
        .catch(() => undefined);
      void loadAnalytics(timeframe, viewTag, accountFilter);
    }
  }, [user?.role, loadAnalytics, timeframe, viewTag, accountFilter]);

  const isAdmin = user?.role === 'head' || user?.role === 'co-head';

  return (
    <div className="min-h-screen bg-black text-zinc-100 aurora-bg">
      {/* ─── Header ─── */}
      <header className="sticky top-0 z-40 border-b border-zinc-800/60 bg-black/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:px-8">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 shadow-lg shadow-emerald-500/30">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 7h15a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
                <path d="M16 12h.01M3 7l2-3h11l2 3" />
                <text x="12" y="15.5" fontSize="9" fontWeight="bold" textAnchor="middle" fill="white" stroke="none">₹</text>
              </svg>
            </div>
            <div>
              <h1 className="font-logo text-lg font-extrabold tracking-tight text-white">
                Wallet<span className="text-emerald-400">Pulse</span>
              </h1>
              <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-zinc-500">
                Financial Intelligence
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-2">
            <HealthPill health={health} />

            <TimeframeToggle value={timeframe} onChange={setTimeframe} />

            {isAdmin && viewOptions.length > 1 && (
              <select
                value={viewTag ?? ''}
                onChange={(e) => setViewTag(e.target.value || null)}
                title="View scope"
                className="h-10 rounded-xl border border-zinc-800 bg-zinc-900/80 px-2.5 text-xs font-medium text-zinc-300 outline-none transition hover:border-zinc-700 focus:border-zinc-600"
              >
                {viewOptions.map((o) => (
                  <option key={o.label} value={o.tag ?? ''}>
                    {o.label}
                  </option>
                ))}
              </select>
            )}

            {user && (
              <select
                value={accountFilter}
                onChange={(e) => setAccountFilter(e.target.value)}
                title="Filter by bank account"
                className="h-10 rounded-xl border border-zinc-800 bg-zinc-900/80 px-2.5 text-xs font-medium text-zinc-300 outline-none transition hover:border-zinc-700 focus:border-zinc-600"
              >
                <option value="">All accounts</option>
                {accounts.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            )}

            {user && (
              <Button variant="ghost" size="md" onClick={() => void loadAnalytics(timeframe, viewTag, accountFilter)} title="Refresh data">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-2.64-6.36M21 3v6h-6" /></svg>
              </Button>
            )}

            {isAdmin && (
              <Button variant="secondary" size="md" onClick={() => setMembersOpen(true)}>Family</Button>
            )}
            {user && (
              <Button variant="secondary" size="md" onClick={() => setSourcesOpen(true)}>Documents</Button>
            )}
            {user && (
              <Button variant="secondary" size="md" onClick={() => setTransactionsOpen(true)}>Transactions</Button>
            )}
            {user && (
              <Button variant="secondary" size="md" onClick={() => setCategorizeOpen(true)}>Categories</Button>
            )}
            {user && (
              <Button variant="accent" size="md" onClick={() => setAiSettingsOpen(true)}>AI</Button>
            )}
            {user && (
              <Button variant="ghost" size="md" onClick={() => setDebugOpen(true)} title="Debug statements">🐞 Debug</Button>
            )}
            {user && (
              <Button variant="secondary" size="md" onClick={() => setFireflyOpen(true)}>Firefly</Button>
            )}
            {user && (
              <Button variant="ghost" size="md" onClick={() => setChangePwOpen(true)}>Password</Button>
            )}

            <Button
              variant={user ? 'primary' : 'primary'}
              size="md"
              onClick={user ? handleLogout : () => setLoginOpen(true)}
            >
              {user ? 'Logout' : 'Login'}
            </Button>

            <FileUpload onSuccess={handleIngestSuccess} />
          </div>
        </div>
      </header>

      {/* ─── Main Content ─── */}
      <main className="mx-auto max-w-7xl space-y-6 px-4 py-6 sm:px-6 lg:px-8">
        {error && <ErrorBanner message={error} onRetry={() => loadAnalytics(timeframe, viewTag)} />}

        {loading && !analytics ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-28 animate-pulse rounded-xl border border-zinc-800 bg-zinc-900/40" />
            ))}
          </div>
        ) : (
          analytics && (
            <>
              <SummaryCards data={analytics} />

              {aiInsights ? (
                <div className="rounded-xl border border-indigo-500/30 bg-indigo-500/10 p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-indigo-300">
                      🤖 Last Upload · AI Analysis{aiInsights.provider ? ` · ${aiInsights.provider}` : ''}
                    </h3>
                    <span className="text-[10px] text-indigo-300/70">
                      {aiInsights.at ? new Date(aiInsights.at).toLocaleString() : ''}
                    </span>
                  </div>
                  {aiInsights.spending_traps?.length || aiInsights.investment_suggestions?.length ? (
                    <>
                      {aiInsights.spending_traps?.length ? (
                        <ul className="list-disc space-y-1 pl-4 text-xs text-zinc-300">
                          {aiInsights.spending_traps.map((t, i) => (
                            <li key={i}>{t}</li>
                          ))}
                        </ul>
                      ) : null}
                      {aiInsights.investment_suggestions?.length ? (
                        <ul className="mt-2 list-disc space-y-1 pl-4 text-xs text-emerald-300">
                          {aiInsights.investment_suggestions.map((t, i) => (
                            <li key={i}>{t}</li>
                          ))}
                        </ul>
                      ) : null}
                    </>
                  ) : (
                    <p className="text-xs text-zinc-400">No notable spending patterns in your last upload.</p>
                  )}
                </div>
              ) : null}

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
                {/* Chart View Controls */}
                <div className="lg:col-span-2 xl:col-span-3 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wider text-zinc-600">View</span>
                    <div className="flex rounded-lg border border-zinc-800 bg-zinc-900/60 p-0.5">
                      {[
                        { key: 'overview', label: 'Overview' },
                        { key: 'main', label: 'By Category' },
                        { key: 'category', label: 'Detailed' },
                      ].map((view) => (
                        <button
                          key={view.key}
                          onClick={() => setChartGroupBy(view.key as typeof chartGroupBy)}
                          className={`rounded-md px-3 py-1.5 text-xs font-medium transition-all ${
                            chartGroupBy === view.key
                              ? 'bg-zinc-800 text-zinc-200 shadow-sm'
                              : 'text-zinc-500 hover:text-zinc-400'
                          }`}
                        >
                          {view.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {chartGroupBy === 'main' && (
                    <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-600">
                      Grouped by prime categories
                    </div>
                  )}
                </div>

                <div className="rounded-xl border border-zinc-800/60 bg-zinc-900/20 p-5">
                  <h2 className="mb-4 text-xs font-semibold uppercase tracking-wider text-zinc-500">
                    {chartGroupBy === 'overview' ? 'Income vs Spending' : 
                     chartGroupBy === 'main' ? 'Spending by Prime Category' : 
                     'Spending by Category'}
                  </h2>
                  <SpendingBarChart 
                    periods={analytics.periods} 
                    mainCategories={categoryHierarchy}
                    groupBy={chartGroupBy === 'main' ? 'main' : 'category'}
                  />
                </div>
                <div className="rounded-xl border border-zinc-800/60 bg-zinc-900/20 p-5">
                  <h2 className="mb-4 text-xs font-semibold uppercase tracking-wider text-zinc-500">
                    {chartGroupBy === 'overview' ? 'Trend Analysis' : 
                     chartGroupBy === 'main' ? 'Category Trends' : 
                     'Detailed Trends'}
                  </h2>
                  <TrendLineChart 
                    periods={analytics.periods} 
                    mainCategories={categoryHierarchy}
                    groupBy={chartGroupBy}
                  />
                </div>
                <div className="rounded-xl border border-zinc-800/60 bg-zinc-900/20 p-5">
                  <h2 className="mb-4 text-xs font-semibold uppercase tracking-wider text-zinc-500">
                    {chartGroupBy === 'main' ? 'Prime Split' : 'Category Split'}
                  </h2>
                  <SpendingPieChart
                    periods={analytics.periods}
                    groupBy={chartGroupBy === 'main' ? 'main' : 'category'}
                  />
                </div>
              </div>
            </>
          )
        )}

        <section>
          <h2 className="mb-4 text-base font-bold text-zinc-100">AI Insights</h2>
          {suggestionsError && !suggestions && (
            <div className="mb-4">
              <ErrorBanner message={suggestionsError} onRetry={loadSuggestions} />
            </div>
          )}
          <InsightsPanel data={suggestions} loading={loading && !suggestions} />
        </section>

        <footer className="pb-8 pt-4 text-center text-[11px] text-zinc-700">
          pdf.js → Cloudflare Worker middleware → Firefly III · AI: Ollama / Groq / Gemini / OpenRouter / Cloudflare AI
        </footer>
      </main>

      <ErrorBoundary label="modals">
      <LoginModal
        open={loginOpen}
        onClose={() => {
          setLoginOpen(false);
          setLoginMessage(null);
        }}
        onLoggedIn={handleLoggedIn}
        message={loginMessage}
      />
      <MembersModal
        open={membersOpen}
        onClose={() => setMembersOpen(false)}
        onChanged={handleMembersChanged}
      />
      <ChangePasswordModal
        open={changePwOpen}
        onClose={() => setChangePwOpen(false)}
        username={user?.username ?? 'user'}
      />
      <CategorizeModal
        open={categorizeOpen}
        onClose={() => setCategorizeOpen(false)}
        onChanged={handleIngestSuccess}
        onViewTransactions={(category) => {
          setTxFilter(category);
          setTransactionsOpen(true);
        }}
      />
      <SourcesPanel
        open={sourcesOpen}
        onClose={() => setSourcesOpen(false)}
        onChanged={handleIngestSuccess}
      />
      <TransactionList
        open={transactionsOpen}
        onClose={() => { setTransactionsOpen(false); setTxFilter(null); }}
        onChanged={handleIngestSuccess}
        filterCategory={txFilter}
      />
      <AISettingsModal
        open={aiSettingsOpen}
        onClose={() => setAiSettingsOpen(false)}
      />
      <DebugPanel
        open={debugOpen}
        onClose={() => setDebugOpen(false)}
      />
      <FireflyHub
        open={fireflyOpen}
        onClose={() => setFireflyOpen(false)}
      />
      </ErrorBoundary>
    </div>
  );
}

function HealthPill({ health }: { health: HealthResponse | null }) {
  let label = '...';
  let cls = 'border-zinc-800 text-zinc-600';
  if (health) {
    const ok = health.ok && health.db.ok;
    label = ok ? 'Online' : 'Degraded';
    cls = ok
      ? 'border-emerald-500/30 text-emerald-400'
      : 'border-amber-500/40 text-amber-400';
  }
  return (
    <span
      className={`hidden rounded-lg border border-zinc-800 bg-zinc-900 px-2.5 py-1.5 text-[11px] font-semibold sm:inline-block ${cls}`}
      title={health ? `Firefly: ${health.db.ok ? 'ok' : health.db.error ?? 'down'} · AI: ${JSON.stringify(health.ai)}` : 'Health unavailable'}
    >
      {label}
    </span>
  );
}
